import { Injectable, Type, inject } from '@angular/core';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { TelXLContentPreviewModalComponent } from '../content-preview-modal.component';

export type PreviewComponentType = Type<{ context: IPreviewContext }>;

export interface IPreviewContext {
    url: string;
    contentType: string;
    fileName?: string;
}

type SizeOrFn =
    | NgbModalOptions['size']
    | ((ctx: IPreviewContext) => NgbModalOptions['size']);

interface IPreviewResolver {
    contentTypes?: (string | RegExp)[];
    exts?: string[];
    match?: (ctx: IPreviewContext) => boolean;
    load: () => Promise<Type<any>>;
    size?: SizeOrFn;
}

function normaliseCt(ct?: string | null): string | null {
    if (!ct) return null;
    return ct.split(';', 1)[0]?.trim().toLowerCase() || null;
}

function getExt(ctx: IPreviewContext): string | null {
    const source = ctx.fileName ?? '';
    const qIndex = source.indexOf('?');
    const clean = (
        qIndex >= 0 ? source.slice(0, qIndex) : source
    ).toLowerCase();
    const lastSlash = clean.lastIndexOf('/');
    const base = clean.slice(lastSlash + 1);
    const dot = base.lastIndexOf('.');

    if (dot <= 0 || dot === base.length - 1) return null;
    return base.slice(dot + 1);
}

function matchesList<T extends string | RegExp>(
    value: string,
    list?: T[],
): boolean {
    if (!value || !list?.length) return false;
    return list.some(item =>
        typeof item === 'string'
            ? value === item.toLowerCase()
            : item.test(value),
    );
}

function resolverMatches(
    resolver: IPreviewResolver,
    ctx: IPreviewContext,
): boolean {
    const ct = normaliseCt(ctx.contentType ?? null);
    const ext = getExt(ctx);

    if (ct && matchesList(ct, resolver.contentTypes)) return true;

    if (
        ext &&
        matchesList(
            ext,
            resolver.exts?.map(e => e.toLowerCase()),
        )
    ) {
        return true;
    }

    if (resolver.match && resolver.match(ctx)) return true;

    return false;
}

function resolveSize(
    resolver: IPreviewResolver,
    ctx: IPreviewContext,
): NgbModalOptions['size'] {
    const s = resolver.size;
    return typeof s === 'function' ? s(ctx) : (s ?? 'md');
}

const RESOLVERS: IPreviewResolver[] = [
    {
        contentTypes: [/^image\//],
        exts: [
            'png',
            'jpg',
            'jpeg',
            'jpe',
            'gif',
            'bmp',
            'webp',
            'tif',
            'tiff',
            'svg',
        ],
        load: async () =>
            (await import('../previewers/image-preview.component'))
                .ImagePreviewComponent,
        size: 'md',
    },
    {
        contentTypes: ['application/pdf'],
        exts: ['pdf'],
        load: async () =>
            (await import('../previewers/pdf-preview.component'))
                .PDFPreviewComponent,
        size: 'xl',
    },
    {
        contentTypes: [/^video\//],
        exts: ['mp4', 'm4v', 'webm', 'ogg', 'ogv', 'mov'],
        load: async () =>
            (await import('../previewers/video-preview.component'))
                .VideoPreviewComponent,
        size: 'xl',
    },
    {
        contentTypes: [
            /^text\//,
            'application/json',
            'application/csv',
            'text/csv',
        ],
        exts: ['txt', 'log', 'md', 'json', 'csv', 'tsv', 'xml', 'yaml', 'yml'],
        load: async () =>
            (await import('../previewers/text-preview.component'))
                .TextPreviewComponent,
        size: ctx => {
            const ext = getExt(ctx);
            return ext === 'csv' || ext === 'tsv' ? 'lg' : 'md';
        },
    },
    {
        contentTypes: [/^audio\//],
        exts: ['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac'],
        load: async () =>
            (await import('../previewers/audio-preview.component'))
                .AudioPreviewComponent,
        size: 'md',
    },
];

const FALLBACK: IPreviewResolver = {
    load: async () =>
        (await import('../previewers/generic-preview.component'))
            .GenericPreviewComponent,
    size: 'md',
};

@Injectable({ providedIn: 'root' })
export class TelXLContentPreviewService {
    readonly #modal = inject(NgbModal);

    async open(context: IPreviewContext, options?: NgbModalOptions) {
        const resolver =
            RESOLVERS.find(r => resolverMatches(r, context)) ?? FALLBACK;

        const component = await resolver.load();

        const ref = this.#modal.open(TelXLContentPreviewModalComponent, {
            size: resolveSize(resolver, context),
            centered: true,
            backdrop: 'static',
            keyboard: true,
            ...options,
        });

        ref.componentInstance.context = context;
        ref.componentInstance.component = component;

        return ref.result;
    }

    async resolveForInline(source: {
        url?: string;
        contentType?: string;
        fileName?: string;
    }): Promise<{ component: PreviewComponentType; ctx: IPreviewContext }> {
        const ctx: IPreviewContext = {
            url: source.url ?? '',
            contentType: source.contentType ?? '',
            fileName: source.fileName ?? 'Preview',
        } as IPreviewContext;

        const resolver =
            RESOLVERS.find(r => resolverMatches(r, ctx)) ?? FALLBACK;
        const component = (await resolver.load()) as PreviewComponentType;

        return { component, ctx };
    }

    canPreview(contentType?: string, fileName?: string): boolean {
        const ctx: IPreviewContext = {
            fileName: fileName,
            contentType: contentType ?? '',
        } as IPreviewContext;

        return RESOLVERS.some(r => resolverMatches(r, ctx));
    }
}
